/**
 * Guild ladder — the guild-level progression funded by premium subscriptions.
 * The user-facing name ("Guild Renown") lives ONLY in locale strings
 * (`patreon:guild-ladder.name`); code stays name-agnostic.
 *
 * Single source of truth for how subscriptions convert into ladder points and
 * which perks unlock at each rung. Consumed by:
 * - `{game}/src/backend/services/patronLevelServiceFactory.ts` (weight computation, featured recruitment)
 * - retail scan-driver and Era/Classic `scanPatreonMembers` tools (daily auto-scan gating)
 * - `{game}/src/web/controllers/userSettingsControllerFactory.ts` (cosmetic perk gating)
 * - shared frontend premium marketing and guild page components
 */

/**
 * Ladder points contributed per active subscription, keyed by patron level (see PATRON_LEVELS in ./patron).
 *
 * Caveat: the patron cache only tracks guilds with at least one Revered+ subscriber
 * (`hasPremium3OrHigher` in patronLevelServiceFactory), so Friendly/Honored points count
 * once a guild is tracked but cannot put a guild on the ladder by themselves.
 */
export const GUILD_LADDER_POINTS_BY_PREMIUM_LEVEL: Record<number, number> = {
  1: 1, // Friendly (contributes, unadvertised)
  2: 1, // Honored (contributes, unadvertised)
  3: 5, // Revered
  4: 10, // Exalted
  5: 20 // Illustrious
};

/** Marketing-surface convenience: the advertised tiers only, keyed by tier slug. */
export const GUILD_LADDER_POINTS_BY_TIER_SLUG = {
  revered: GUILD_LADDER_POINTS_BY_PREMIUM_LEVEL[3],
  exalted: GUILD_LADDER_POINTS_BY_PREMIUM_LEVEL[4],
  illustrious: GUILD_LADDER_POINTS_BY_PREMIUM_LEVEL[5]
} as const;

export type TGuildLadderPerkSlug =
  | 'guild-premium-banners'
  | 'featured-recruitment'
  | 'animated-banners'
  | 'brand-color'
  | 'guild-vanity-url'
  | 'daily-auto-scan'
  | 'guild-backdrop';

export type TGuildLadderRung = {
  /** Stable rung id, used in locale keys and analytics. */
  rung: number;
  /** Minimum guild ladder points required to unlock this rung. */
  threshold: number;
  perks: readonly TGuildLadderPerkSlug[];
};

export const GUILD_LADDER_RUNGS: readonly TGuildLadderRung[] = [
  // Rung 1 lands on the tracking gate (5 pts / one Revered) so a guild's first
  // subscription unlocks a perk the instant the guild becomes visible. Every
  // threshold is same-or-cheaper than the prior 10/20/40 ladder, so no guild
  // that had a perk loses it. Perks are placed so their guild threshold never
  // undercuts the personal price of the same cosmetic (animated headers, a
  // personal Illustrious/$20 perk, stay at 20) and so each sold tier solo-unlocks
  // an escalating rung ($5→Lv.1, $10→Lv.2, $20→Lv.3).
  { rung: 1, threshold: 5, perks: ['guild-premium-banners'] },
  { rung: 2, threshold: 10, perks: ['guild-vanity-url', 'brand-color'] },
  { rung: 3, threshold: 20, perks: ['featured-recruitment', 'animated-banners'] },
  { rung: 4, threshold: 40, perks: ['daily-auto-scan', 'guild-backdrop'] }
];

/**
 * DOM id of the guild-ladder ("Guild Renown") perks section on the /premium
 * marketing page. Deep links (e.g. the guild-page renown bar) target it so the
 * page scrolls straight to the perks breakdown.
 */
export const GUILD_LADDER_PREMIUM_ANCHOR = 'guild-renown-perks';

/**
 * Pseudo-feature strings for cosmetic perks, consumed by the existing
 * `required_feature_checks` machinery (string features are precedented:
 * `claimed_guild`, `unlock_8th_anniversary_banner`, raw product codes).
 * Perks enforced directly by threshold (featured recruitment, auto-scan)
 * have no pseudo-feature.
 */
export enum GroupPerkFeature {
  StaticBanners = 'group_perk_static_banners',
  AnimatedBanners = 'group_perk_animated_banners',
  BrandColor = 'group_perk_brand_color',
  PageBackdrop = 'group_perk_page_backdrop'
}

const GROUP_PERK_FEATURE_BY_PERK_SLUG: Partial<Record<TGuildLadderPerkSlug, GroupPerkFeature>> = {
  'guild-premium-banners': GroupPerkFeature.StaticBanners,
  'animated-banners': GroupPerkFeature.AnimatedBanners,
  'brand-color': GroupPerkFeature.BrandColor,
  'guild-backdrop': GroupPerkFeature.PageBackdrop
};

/** Whether a feature-check string is one of the guild-ladder pseudo-features. */
export function isGroupPerkFeature(feature: string): boolean {
  return (Object.values(GroupPerkFeature) as string[]).includes(feature);
}

/**
 * M+ team featured recruitment follows the guild ladder (product decision: guild &
 * team share one ladder; where they differ, team follows guild). Derived from the
 * ladder threshold so a future rung move carries the team bar along automatically.
 */
export const TEAM_FEATURED_RECRUITMENT_MIN_POINTS = getGuildLadderPerkThreshold('featured-recruitment');

/** Minimum guild renown points to create/resolve a guild vanity URL (matches rung 2). */
export const GUILD_VANITY_MIN_POINTS = 10;

/**
 * Minimum M+ team points to create/resolve a team vanity URL. Team vanity now
 * follows the guild vanity gate (product decision: where guild & team perks
 * differ, team follows guild), so this references GUILD_VANITY_MIN_POINTS to stay
 * in lockstep with any future guild-rung moves.
 */
export const TEAM_VANITY_MIN_POINTS = GUILD_VANITY_MIN_POINTS;

/** The pseudo-features a guild at `points` has unlocked, for merging into feature-check lists. */
export function getGroupPerkFeatures(points: number): string[] {
  const features: string[] = [];
  for (const { threshold, perks } of GUILD_LADDER_RUNGS) {
    if (points < threshold) {
      continue;
    }
    for (const perk of perks) {
      const feature = GROUP_PERK_FEATURE_BY_PERK_SLUG[perk];
      if (feature) {
        features.push(feature);
      }
    }
  }
  return features;
}

export function getGuildLadderRungForPerk(perk: TGuildLadderPerkSlug): TGuildLadderRung {
  const rung = GUILD_LADDER_RUNGS.find((entry) => entry.perks.includes(perk));
  if (!rung) {
    throw new Error(`Unknown guild ladder perk: ${perk}`);
  }
  return rung;
}

export function getGuildLadderPerkThreshold(perk: TGuildLadderPerkSlug): number {
  return getGuildLadderRungForPerk(perk).threshold;
}

/** The highest rung reached at `points`, or null below the first rung. */
export function getGuildLadderRungForPoints(points: number): TGuildLadderRung | null {
  let reached: TGuildLadderRung | null = null;
  for (const rung of GUILD_LADDER_RUNGS) {
    if (points >= rung.threshold) {
      reached = rung;
    }
  }
  return reached;
}

/** The next rung above `points`, or null when the top rung is reached. */
export function getNextGuildLadderRung(points: number): TGuildLadderRung | null {
  return GUILD_LADDER_RUNGS.find((rung) => points < rung.threshold) ?? null;
}

/**
 * Points ($/mo) between prestige levels above the top perk rung. Set to one
 * Illustrious sub ($20) so each prestige level is a meaningful, reachable step.
 * Display-only — no perks attach past the top rung; tunable.
 */
export const GUILD_RENOWN_PRESTIGE_STEP = 20;

export type TGuildRenownLevel = {
  /** Displayed level: 0 below the first rung, 1..N for the perk rungs, then endless prestige levels N+1, N+2, … */
  level: number;
  /** Points at which the current level began. */
  currentThreshold: number;
  /** Points that reach the next level (always defined — the ladder never dead-ends). */
  nextThreshold: number;
  /** True once past the top perk rung: the level keeps climbing as pure status, with no new perks. */
  isPrestige: boolean;
};

/**
 * The guild's renown level for display, extending past the top perk rung with
 * endless prestige levels so the progress indicator never shows a dead "MAX".
 * Perk gating still stops at the top rung (see getGroupPerkFeatures / the direct
 * threshold checks) — this drives the level number and "next level" target only.
 */
export function getGuildRenownLevel(points: number): TGuildRenownLevel {
  const firstRung = GUILD_LADDER_RUNGS[0];
  const topRung = GUILD_LADDER_RUNGS[GUILD_LADDER_RUNGS.length - 1];

  if (points < firstRung.threshold) {
    return { level: 0, currentThreshold: 0, nextThreshold: firstRung.threshold, isPrestige: false };
  }

  if (points < topRung.threshold) {
    const current = getGuildLadderRungForPoints(points)!;
    const next = getNextGuildLadderRung(points)!;
    return { level: current.rung, currentThreshold: current.threshold, nextThreshold: next.threshold, isPrestige: false };
  }

  // At or above the top rung: extend with endless, perk-less prestige levels.
  const prestigeSteps = Math.floor((points - topRung.threshold) / GUILD_RENOWN_PRESTIGE_STEP);
  return {
    level: topRung.rung + prestigeSteps,
    currentThreshold: topRung.threshold + prestigeSteps * GUILD_RENOWN_PRESTIGE_STEP,
    nextThreshold: topRung.threshold + (prestigeSteps + 1) * GUILD_RENOWN_PRESTIGE_STEP,
    isPrestige: prestigeSteps > 0
  };
}

/**
 * Fraction (0–1) of the guild renown progress bar to fill for a guild at `points`.
 *
 * While climbing the perk rungs the bar is divided into equal per-rung segments
 * (visual clarity over proportional thresholds); within the active segment the
 * fill advances proportionally from the last threshold to the next, reaching a
 * full bar once every perk is unlocked at the top rung.
 *
 * Past the perk ladder the level keeps climbing through endless, perk-less
 * prestige steps — there is no room for more equal segments, so the bar restarts
 * each prestige level and fills proportionally toward the next one. Without this
 * it would sit pinned at 100% and stop reflecting progress during prestige.
 */
export function getGuildRenownFillFraction(points: number): number {
  const renown = getGuildRenownLevel(points);
  if (renown.isPrestige) {
    const span = renown.nextThreshold - renown.currentThreshold;
    return span > 0 ? (points - renown.currentThreshold) / span : 1;
  }

  const rungs = GUILD_LADDER_RUNGS;
  const segment = 1 / rungs.length;

  if (points < rungs[0].threshold) {
    return (points / rungs[0].threshold) * segment;
  }
  for (let i = 0; i < rungs.length; i++) {
    const next = rungs[i + 1];
    if (!next) {
      return 1; // at the top rung: every perk unlocked, bar full
    }
    if (points < next.threshold) {
      const within = (points - rungs[i].threshold) / (next.threshold - rungs[i].threshold);
      return (i + 1 + within) * segment;
    }
  }
  return 1;
}
